import { movieCategories } from "@/mocks/categories";
import type { Movie } from "@/mocks/movies";

export const getCategoryBySlug = (slug: string) =>
  movieCategories.find((item) => item.slug === slug);

export const getCategoryName = (slug: string) =>
  getCategoryBySlug(slug)?.name ?? slug;

export const formatViews = (views: number) => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
};

export type SortKey = "latest" | "hot" | "rating";

export const sortMovies = (list: Movie[], sort: SortKey): Movie[] => {
  const sorted = [...list];
  if (sort === "hot") return sorted.sort((a, b) => b.views - a.views);
  if (sort === "rating") return sorted.sort((a, b) => b.rating - a.rating);
  return sorted.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
};

export interface FilterOptions {
  genre?: string;
  year?: string;
  region?: string;
}

export const filterMovies = (list: Movie[], options: FilterOptions): Movie[] =>
  list.filter((movie) => {
    if (options.genre && options.genre !== "全部" && !movie.genres.includes(options.genre)) {
      return false;
    }
    if (options.year && options.year !== "全部" && String(movie.year) !== options.year) {
      return false;
    }
    if (options.region && options.region !== "全部" && movie.region !== options.region) {
      return false;
    }
    return true;
  });

export const searchMovies = (list: Movie[], keyword: string): Movie[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return list.filter(
    (movie) =>
      movie.title.toLowerCase().includes(q) ||
      movie.enTitle.toLowerCase().includes(q) ||
      movie.synopsis.toLowerCase().includes(q) ||
      movie.director.toLowerCase().includes(q) ||
      movie.cast.some((name) => name.toLowerCase().includes(q)),
  );
};

export const getRelatedMovies = (list: Movie[], current: Movie, limit = 6): Movie[] => {
  const scored = list
    .filter((movie) => movie.id !== current.id)
    .map((movie) => ({
      movie,
      score: movie.genres.filter((g) => current.genres.includes(g)).length,
    }))
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, limit).map((item) => item.movie);
};

export const paginate = <T,>(list: T[], page: number, pageSize: number) => {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    currentPage: safePage,
    total: list.length,
  };
};